import type { EffectKeyframe, EffectParameters, InterpolationMode, KeyframedParameter } from './types';

/** Cubic ease-in (0→1). */
export function easeIn(t: number): number {
  return t * t * t;
}

/** Cubic ease-out (0→1). */
export function easeOut(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}

/** Cubic Bezier easing (approximate De Casteljau). */
export function cubicBezier(
  t: number,
  cp1: [number, number] = [0.42, 0],
  cp2: [number, number] = [0.58, 1],
): number {
  const cx = 3 * cp1[0];
  const bx = 3 * (cp2[0] - cp1[0]) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * cp1[1];
  const by = 3 * (cp2[1] - cp1[1]) - cy;
  const ay = 1 - cy - by;
  const sample = (u: number) => ((ax * u + bx) * u + cx) * u;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 12; i += 1) {
    const mid = (lo + hi) / 2;
    if (sample(mid) < t) lo = mid;
    else hi = mid;
  }
  const u = (lo + hi) / 2;
  return ((ay * u + by) * u + cy) * u;
}

export function applyInterpolation(
  t: number,
  mode: InterpolationMode,
  kf?: EffectKeyframe,
): number {
  const clamped = Math.max(0, Math.min(1, t));
  switch (mode) {
    case 'ease_in':
      return easeIn(clamped);
    case 'ease_out':
      return easeOut(clamped);
    case 'bezier':
      return cubicBezier(clamped, kf?.cp1, kf?.cp2);
    case 'linear':
    default:
      return clamped;
  }
}

/** Interpolate between two keyframes. */
export function interpolateKeyframePair(
  a: EffectKeyframe,
  b: EffectKeyframe,
  time: number,
  mode: InterpolationMode = 'linear',
): number {
  if (b.time <= a.time) return a.value;
  const span = b.time - a.time;
  const localT = (time - a.time) / span;
  const eased = applyInterpolation(localT, mode, b);
  return a.value + (b.value - a.value) * eased;
}

/** Evaluate a scalar parameter at local effect time. */
export function evaluateScalar(
  param: number | KeyframedParameter | undefined,
  localTime: number,
  fallback: number,
): number {
  if (param == null) return fallback;
  if (typeof param === 'number') return param;

  const { staticValue, keyframes, interpolation = 'linear' } = param;
  if (!keyframes?.length) {
    return staticValue ?? fallback;
  }

  const sorted = [...keyframes].sort((x, y) => x.time - y.time);
  if (localTime <= sorted[0].time) return sorted[0].value;
  const last = sorted[sorted.length - 1];
  if (localTime >= last.time) return last.value;

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (localTime >= a.time && localTime <= b.time) {
      return interpolateKeyframePair(a, b, localTime, interpolation);
    }
  }
  return staticValue ?? fallback;
}

/**
 * Canonical keyframe evaluator (Phase 1).
 * `interpolate(time, keyframes)` — used by every effect parameter track.
 */
export function interpolate(
  time: number,
  keyframes: EffectKeyframe[],
  mode: InterpolationMode = 'linear',
  fallback = 0,
): number {
  return evaluateScalar({ keyframes, interpolation: mode }, time, fallback);
}

/** Resolve all effect parameters at a given local time. */
export function evaluateEffectParameters(
  parameters: EffectParameters,
  keyframeTracks: Record<string, EffectKeyframe[]> | undefined,
  localTime: number,
  defaults: Record<string, number>,
): Record<string, number> {
  const out: Record<string, number> = { ...defaults };

  for (const [key, def] of Object.entries(defaults)) {
    const track = keyframeTracks?.[key];
    const param = parameters[key];
    if (track?.length) {
      out[key] = evaluateScalar({ keyframes: track, interpolation: 'linear' }, localTime, def);
    } else {
      out[key] = evaluateScalar(param as number | KeyframedParameter, localTime, def);
    }
  }

  // Extra non-default keys
  for (const [key, param] of Object.entries(parameters)) {
    if (out[key] !== undefined) continue;
    if (typeof param === 'number') out[key] = param;
    else if (param && typeof param === 'object') {
      out[key] = evaluateScalar(param, localTime, 0);
    }
  }

  return out;
}
